import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CourierUserApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../../shared/api-config';
import { getCourier, getCourierSetting } from '../../state/courierSlice';
import {
  getIsAuthenticated,
  getUser,
  login,
  logout,
} from '../../state/authSlice';
import { AccessToken } from '@shared/sdk-v1/models';
import { showErrorToast, showSuccessToast } from '../../utils/toast';
import { extractApiError } from '../../utils/apiError';
import { useTrackEvent } from '../../utils/track';
import { deriveMailboxNumber } from '../../utils/mailbox';

const GENERIC_LOGIN_ERROR = 'Invalid email or password.';

/**
 * LoopBack's own copy for these codes is written for a developer reading a log
 * ("login failed"), not for a customer staring at a form. Everything else keeps
 * the backend's message — that's what finally makes real causes visible, e.g.
 * the Turnstile middleware's "CAPTCHA verification failed. Please try again."
 */
const LOGIN_ERROR_COPY: Record<string, string> = {
  LOGIN_FAILED: GENERIC_LOGIN_ERROR,
  LOGIN_FAILED_EMAIL_NOT_VERIFIED:
    'Please verify your email address before signing in.',
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const courier = useSelector(getCourier);
  const courierSetting = useSelector(getCourierSetting);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const user = useSelector(getUser);
  const track = useTrackEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVerificationModalOpen, setVerificationModalOpen] = useState(false);

  const clearError = useCallback(() => setError(null), []);

  const failLogin = (message: string) => {
    setError(message);
    showErrorToast({ title: 'Login Failed', message });
    return { success: false, needsVerification: false };
  };

  const handleLogin = async (email: string, password: string, turnstileToken?: string, turnstileSiteKey?: string) => {
    setError(null);

    if (!courier?.realm) {
      setError('Courier configuration not found');
      showErrorToast({
        title: 'Error',
        message: 'Courier configuration not found',
      });
      return { success: false, needsVerification: false };
    }

    const authApi = new CourierUserApi(apiConfig());
    try {
      setIsLoading(true);
      const accessToken: AccessToken = (await authApi.courierUserLogin({
        credentials: {
          email: email,
          password: password,
          realm: courier.realm,
          ...(turnstileToken ? { turnstileToken } : {}),
          ...(turnstileSiteKey ? { turnstileSiteKey } : {}),
        },
        // The /CourierUsers/login endpoint only honors the simple include form;
        // a nested include object is silently ignored and returns no user,
        // which breaks auth. Use 'user' so accessToken.user is populated.
        include: 'user',
      })) as AccessToken;

      if (accessToken.id) {
        // Check if user needs email verification if courier requires verification
        const requireVerification = courierSetting?.requireVerification;
        const needsEmailVerification =
          accessToken.user && !accessToken.user.emailVerified;

        if (needsEmailVerification && requireVerification) {
          setIsLoading(false);
          return {
            success: false,
            needsVerification: true,
            accessToken,
            userEmail: email,
            userId: accessToken.user?.id?.toString() || '',
          };
        } else {
          dispatch(login(accessToken));
          track('login', {
            mailbox_number: deriveMailboxNumber(
              accessToken.user,
              courierSetting?.prefix,
              courier?.mailboxPaddingLength
            ),
          });
          setIsLoading(false);
          setVerificationModalOpen(true); // Open the verification modal
          return { success: true, needsVerification: false };
        }
      }
      // Login resolved without a token — treat it as a rejection rather than
      // returning silently, which left the form looking like nothing happened.
      setIsLoading(false);
      return failLogin(GENERIC_LOGIN_ERROR);
    } catch (err: any) {
      setIsLoading(false);
      console.error('Login error:', err);
      const { message, code } = await extractApiError(err, GENERIC_LOGIN_ERROR);
      return failLogin((code && LOGIN_ERROR_COPY[code]) || message);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  return {
    isAuthenticated,
    user,
    isLoading,
    error,
    clearError,
    handleLogin,
    handleLogout,
    courier,
    courierSetting,
    isVerificationModalOpen,
    setVerificationModalOpen,
  };
};

export default useAuth;
